import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { SIFTVehicle } from '../../types/sift';
import { RdsMiaSummary } from '../client-view/RdsMiaSummary';
import { VMA_FIELD_HELP, VMA_PANEL_NOTE } from '../shared/vmaFieldHelp';
import { VinBuyersCard } from '../shared/VinBuyersCard';
import { VinCohortInsightsCard } from '../shared/VinCohortInsightsCard';
import './VehicleInsightsModal.css';
import { pushUsageContext, track } from '../../telemetry/usage';

interface VehicleInsightsModalProps {
  vehicle: SIFTVehicle;
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  /** Mia's "Where to focus" tab (RDS-3.5); hidden when Mia is off for the user. */
  miaEnabled?: boolean;
}

type TabSection = 
  | 'where-to-focus'
  | 'vehicle-details'
  | 'listing-details' 
  | 'inspection-details'
  | 'bid-details'
  | 'buyers'
  | 'market-insights';

const TAB_SECTIONS = [
  { key: 'where-to-focus' as TabSection, label: 'Where to focus', number: 1 },
  { key: 'vehicle-details' as TabSection, label: 'Vehicle Details', number: 2 },
  { key: 'listing-details' as TabSection, label: 'Listing Details', number: 3 },
  { key: 'inspection-details' as TabSection, label: 'Inspection Details', number: 4 },
  { key: 'bid-details' as TabSection, label: 'Bid Details', number: 5 },
  { key: 'buyers' as TabSection, label: 'Buyers', number: 6 },
  { key: 'market-insights' as TabSection, label: 'Market Insights', number: 7 }
];

export const VehicleInsightsModal: React.FC<VehicleInsightsModalProps> = ({
  vehicle,
  isOpen,
  onClose,
  isLoading = false,
  miaEnabled = false,
}) => {
  const [activeTab, setActiveTab] = useState<TabSection>(miaEnabled ? 'where-to-focus' : 'vehicle-details');
  const tabs = TAB_SECTIONS
    .filter((t) => miaEnabled || t.key !== 'where-to-focus')
    .map((t, i) => ({ ...t, number: i + 1 }));

  const trackedVin = isOpen ? vehicle?.vin : undefined;
  const trackedSeller = vehicle?.seller_id;
  useEffect(() => {
    if (!trackedVin) return undefined;
    track('main', 'vin_details', 'open', { vin: trackedVin, seller_uid: trackedSeller });
    return pushUsageContext('vin_details', { vin: trackedVin, seller_uid: trackedSeller });
  }, [trackedVin, trackedSeller]);

  if (!isOpen) return null;

  // RDS-3.5: Mia's "Where to focus" replaces the old Recommendations tab. No "Continue in Mia"
  // here: the Main View has no Mia chat.
  const renderWhereToFocus = () => (
    <div className="modal-section">
      <RdsMiaSummary key={vehicle.vin} kind="vin" id={vehicle.vin} />
    </div>
  );

  const renderVehicleDetails = () => (
    <div className="modal-section">
      <div className="detail-grid">
        <div className="detail-item">
          <label>VIN</label>
          <span>{vehicle.vin}</span>
        </div>
        <div className="detail-item">
          <label>Year</label>
          <span>{vehicle.model_year}</span>
        </div>
        <div className="detail-item">
          <label>Make</label>
          <span>{vehicle.make}</span>
        </div>
        <div className="detail-item">
          <label>Model</label>
          <span>{vehicle.model}</span>
        </div>
        <div className="detail-item">
          <label>Trim</label>
          <span>{vehicle.trim}</span>
        </div>
        <div className="detail-item">
          <label>Color</label>
          <span>{vehicle.color}</span>
        </div>
        <div className="detail-item">
          <label>Vehicle Type</label>
          <span>{vehicle.vehicle_type}</span>
        </div>
        <div className="detail-item">
          <label>Drivetrain</label>
          <span>{vehicle.drivetrain}</span>
        </div>
        <div className="detail-item">
          <label>Engine</label>
          <span>{vehicle.engine}</span>
        </div>
        <div className="detail-item">
          <label>Body Style</label>
          <span>{vehicle.body_style}</span>
        </div>
        <div className="detail-item">
          <label>Doors</label>
          <span>{vehicle.doors}</span>
        </div>
        <div className="detail-item">
          <label>Fuel Type</label>
          <span>{vehicle.fuel_type}</span>
        </div>
        <div className="detail-item">
          <label>Transmission</label>
          <span>{vehicle.transmission}</span>
        </div>
        <div className="detail-item">
          <label>Mileage</label>
          <span>{vehicle.mileage?.toLocaleString() || 'N/A'}</span>
        </div>
      </div>
    </div>
  );

  const renderListingDetails = () => (
    <div className="modal-section">
      <div className="detail-grid">
        <div className="detail-item">
          <label>Seller ID</label>
          <span>{vehicle.seller_id}</span>
        </div>
        <div className="detail-item">
          <label>Seller Name</label>
          <span>{vehicle.seller_name}</span>
        </div>
        <div className="detail-item">
          <label>Data Source</label>
          <span>{vehicle.data_source || 'N/A'}</span>
        </div>
        <div className="detail-item">
          <label>List Time</label>
          <span>{formatDate(vehicle.list_time)}</span>
        </div>
        <div className="detail-item">
          <label>Days Listed</label>
          <span>{vehicle.list_days}</span>
        </div>
        <div className="detail-item">
          <label>Channel</label>
          <span>{vehicle.channel}</span>
        </div>
        <div className="detail-item">
          <label>Facilitation</label>
          <span>{vehicle.facilitation || 'N/A'}</span>
        </div>
        <div className="detail-item">
          <label>Site Location</label>
          <span>{vehicle.site_location || 'N/A'}</span>
        </div>
        <div className="detail-item">
          <label>List Status</label>
          <span>{vehicle.list_status}</span>
        </div>
        <div className="detail-item">
          <label>Base MMR</label>
          <span>${vehicle.base_mmr?.toLocaleString() || 'N/A'}</span>
        </div>
        <div className="detail-item">
          <label>MMR Range</label>
          <span>{vehicle.mmr_range}</span>
        </div>
        <div className="detail-item">
          <label>Adjusted MMR</label>
          <span>${vehicle.adjusted_mmr?.toLocaleString() || 'N/A'}</span>
        </div>
        <div className="detail-item">
          <label>Floor Price</label>
          <span>${vehicle.floor_price?.toLocaleString() || 'N/A'}</span>
        </div>
        <div className="detail-item">
          <label>Buy Now Price</label>
          <span>${vehicle.buy_now_price?.toLocaleString() || 'N/A'}</span>
        </div>
        <div className="detail-item">
          <label>Avg Daily VDP Views</label>
          <span>{vehicle.average_daily_vdp_views}</span>
        </div>
        <div className="detail-item">
          <label>Total Client VDP Views</label>
          <span>{vehicle.total_client_vdp_views}</span>
        </div>
        <div className="detail-item">
          <label>Client Workbook Saves</label>
          <span>{vehicle.client_workbook_saves}</span>
        </div>
        <div className="detail-item">
          <label>Photo Count</label>
          <span>{vehicle.photo_count}</span>
        </div>
        <div className="detail-item">
          <label>State</label>
          <span>{vehicle.state}</span>
        </div>
        <div className="detail-item">
          <label>Auction</label>
          <span>{vehicle.auction}</span>
        </div>
        <div className="detail-item">
          <label>Seller Acquisition Time</label>
          <span>{formatDate(vehicle.seller_acquisition_time)}</span>
        </div>
        <div className="detail-item">
          <label>Seller Ownership Days</label>
          <span>{vehicle.seller_ownership_days}</span>
        </div>
        <div className="detail-item">
          <label>Seller Acquisition Method</label>
          <span>{vehicle.seller_acquisition_method}</span>
        </div>
        <div className="detail-item">
          <label>Listing Occurrences</label>
          <span>{vehicle.listing_occurences}</span>
        </div>
        <div className="detail-item">
          <label>VDP Link</label>
          <span>
            {vehicle.vdp_url ? (
              <a href={vehicle.vdp_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary, #FFC72C)' }}>VDP Link</a>
            ) : 'N/A'}
          </span>
        </div>
      </div>
      
      {/* Listing History Table */}
      <div className="data-table-section">
        <h4>Listing History</h4>
        {vehicle.listing_history && vehicle.listing_history.length > 0 ? (
          <div className="json-data-table" style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>List Time</th>
                  <th>Status</th>
                  <th>Auction</th>
                  <th>Channel</th>
                  <th>Seller</th>
                  <th>State</th>
                  <th>Adj MMR</th>
                  <th>Buy Now</th>
                  <th>Floor</th>
                  <th>Mileage</th>
                  <th>CR Grade</th>
                  <th>Simulcast</th>
                  <th>VDP Views</th>
                  <th>Workbook Saves</th>
                  <th>VDP</th>
                  <th>Buyer</th>
                  <th>Sold Amt</th>
                  <th>Sold Time</th>
                </tr>
              </thead>
              <tbody>
                {vehicle.listing_history
                  .sort((a, b) => {
                    const dateA = new Date(a.list_time.replace(' UTC', '') + 'Z').getTime();
                    const dateB = new Date(b.list_time.replace(' UTC', '') + 'Z').getTime();
                    return dateB - dateA;
                  })
                  .map((listing, index) => (
                    <tr key={index}>
                      <td>{formatDate(listing.list_time)}</td>
                      <td>{listing.list_status || '-'}</td>
                      <td>{listing.auction || '-'}</td>
                      <td>{listing.channel || '-'}</td>
                      <td title={`ID: ${listing.seller_id || 'N/A'}`}>{listing.seller_name || '-'}</td>
                      <td>{listing.state || '-'}</td>
                      <td>{listing.adj_mmr != null ? `$${Number(listing.adj_mmr).toLocaleString()}` : '-'}</td>
                      <td>{listing.buy_now_price != null ? `$${Number(listing.buy_now_price).toLocaleString()}` : '-'}</td>
                      <td>{listing.floor_price != null ? `$${Number(listing.floor_price).toLocaleString()}` : '-'}</td>
                      <td>{listing.mileage != null ? Number(listing.mileage).toLocaleString() : '-'}</td>
                      <td>{listing.cr_grade || '-'}</td>
                      <td>{listing.simulcast_logged_in != null ? listing.simulcast_logged_in : '-'}</td>
                      <td>{listing.client_vdp_views != null ? listing.client_vdp_views : '-'}</td>
                      <td>{listing.client_workbook_saves != null ? listing.client_workbook_saves : '-'}</td>
                      <td>
                        {listing.vdp_url ? (
                          <a href={listing.vdp_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary, #FFC72C)' }}>
                            VDP Link
                          </a>
                        ) : '-'}
                      </td>
                      <td title={`ID: ${listing.buyer_id || 'N/A'}`}>{listing.buyer_name || '-'}</td>
                      <td>{listing.sold_amt != null ? `$${Number(listing.sold_amt).toLocaleString()}` : '-'}</td>
                      <td>{listing.sold_time ? formatDate(listing.sold_time) : '-'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="no-data">
            No listing history data available
            {vehicle.listing_history && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>
                Raw data: {JSON.stringify(vehicle.listing_history)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderInspectionDetails = () => (
    <div className="modal-section">
      <div className="detail-grid">
        <div className="detail-item">
          <label>CR Grade</label>
          <span>{vehicle.cr_grade || 'N/A'}</span>
        </div>
        <div className="detail-item">
          <label>Inspect Status</label>
          <span>{vehicle.inspect_status || 'N/A'}</span>
        </div>
        <div className="detail-item">
          <label>Inspect Time</label>
          <span>{formatDate(vehicle.inspect_time)}</span>
        </div>
        <div className="detail-item">
          <label>Inspection Channel</label>
          <span>{vehicle.inspection_channel || 'N/A'}</span>
        </div>
        <div className="detail-item">
          <label>Inspection Type</label>
          <span>{vehicle.inspection_type || 'N/A'}</span>
        </div>
        <div className="detail-item">
          <label>CR Link</label>
          {vehicle.cr_link ? (
            <a href={vehicle.cr_link} target="_blank" rel="noopener noreferrer" className="cr-link">
              View Condition Report
            </a>
          ) : (
            <span>N/A</span>
          )}
        </div>
      </div>

      {/* Embedded Condition Report */}
      {vehicle.cr_link && (
        <div className="cr-embed-section">
          <div className="cr-embed-header">
            <h4>Condition Report</h4>
            <a href={vehicle.cr_link} target="_blank" rel="noopener noreferrer" className="cr-external-link">
              Open in New Tab ↗
            </a>
          </div>
          <div className="cr-embed-container">
            <iframe
              src={vehicle.cr_link}
              title="Condition Report"
              className="cr-embed-iframe"
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            />
          </div>
        </div>
      )}
    </div>
  );

  // Helper function to format dates safely
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString || dateString.trim() === '') {
      return 'N/A';
    }
    
    try {
      let date: Date;
      
      // Check if it's a simple date format (YYYY-MM-DD)
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString.trim())) {
        // Simple date format - just show the date
        date = new Date(dateString.trim() + 'T00:00:00Z');
        if (isNaN(date.getTime())) {
          return 'Invalid Date';
        }
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        
        return `${year}-${month}-${day}`;
      } else {
        // Handle the "YYYY-MM-DD HH:mm:ss UTC" format from our data
        const cleanDateString = dateString.replace(' UTC', '').trim();
        date = new Date(cleanDateString + 'Z'); // Add Z to indicate UTC
        
        if (isNaN(date.getTime())) {
          return 'Invalid Date';
        }
        
        // Format as YYYY-MM-DD HH:mm:ss AM/PM (12-hour format)
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        const formattedHours = String(hours).padStart(2, '0');
        
        return `${year}-${month}-${day} ${formattedHours}:${minutes}:${seconds} ${ampm}`;
      }
    } catch {
      return 'Invalid Date';
    }
  };

  const renderBidDetails = () => (
    <div className="modal-section">
      <div className="detail-grid">
        <div className="detail-item">
          <label>Active Bids</label>
          <span>{vehicle.active_bids}</span>
        </div>
        <div className="detail-item">
          <label>Total Bids</label>
          <span>{vehicle.total_bids}</span>
        </div>
        <div className="detail-item">
          <label>Active High Bid</label>
          <span>${vehicle.active_high_bid?.toLocaleString() || 'N/A'}</span>
        </div>
      </div>

      {/* Bid History Table */}
      {vehicle.bid_history && vehicle.bid_history.length > 0 && (
        <div className="data-table-section">
          <h4>Bid History</h4>
          <div className="json-data-table">
            <table>
              <thead>
                <tr>
                  <th>Bid Time</th>
                  <th>Offer Type</th>
                  <th>Buyer</th>
                  <th>Bid Amount</th>
                  <th>Bid Type</th>
                  <th>Channel</th>
                  <th>Status</th>
                  <th>Closed Time</th>
                </tr>
              </thead>
              <tbody>
                {vehicle.bid_history
                  .sort((a, b) => {
                    const dateA = new Date(a.bid_time.replace(' UTC', '') + 'Z').getTime();
                    const dateB = new Date(b.bid_time.replace(' UTC', '') + 'Z').getTime();
                    return dateB - dateA;
                  })
                  .map((bid, index) => (
                    <tr key={index}>
                      <td>{formatDate(bid.bid_time)}</td>
                      <td>{bid.bid_offer_type || '-'}</td>
                      <td>{bid.buyer_name}</td>
                      <td>${bid.bid_amt.toLocaleString()}</td>
                      <td>{bid.bid_type || 'N/A'}</td>
                      <td>{bid.bid_channel || 'N/A'}</td>
                      <td>{bid.bid_status}</td>
                      <td>{formatDate(bid.closed_time)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  const renderBuyers = () => (
    <div className="modal-section">
      <VinBuyersCard vin={vehicle.vin} />
    </div>
  );

  const renderMarketInsights = () => {
    const pct = (v: number | null | undefined) => (v != null ? `${Math.round(v)}%` : 'N/A');
    const money = (v: number | null | undefined) => (v != null ? `$${Math.round(v).toLocaleString()}` : 'N/A');
    const num = (v: number | null | undefined) => (v != null ? Math.round(v).toLocaleString() : 'N/A');
    const str = (v: string | null | undefined) => v || 'N/A';

    const hasSignal =
      vehicle.wholesale_supply_level || vehicle.wholesale_demand_level ||
      vehicle.retail_supply_level || vehicle.retail_demand_level ||
      vehicle.retail_rmi != null || vehicle.retail_spread != null ||
      vehicle.buyer_coverage != null;

    return (
      <div className="modal-section">
        <VinCohortInsightsCard
          vin={vehicle.vin}
          currentAuction={vehicle.auction}
          pricing={{
            floorPrice: vehicle.floor_price,
            buyNowPrice: vehicle.buy_now_price,
            baseMmr: vehicle.base_mmr,
            adjMmr: vehicle.adjusted_mmr,
            mmrRange: vehicle.mmr_range,
            retailValue: vehicle.retail_value,
            retailValueRange: vehicle.retail_value_range,
            retailListPrice: vehicle.retail_list_price,
            retailRmi: vehicle.retail_rmi,
            retailAvgListPrice: vehicle.retail_avg_list_price,
            retailSpread: vehicle.retail_spread,
            retailRetention: vehicle.retail_retention,
          }}
        />

        <div className="detail-grid">
          <div className="detail-item">
            <label title={VMA_FIELD_HELP.wholesale_supply_level}>Wholesale Supply</label>
            <span>{str(vehicle.wholesale_supply_level)}</span>
          </div>
          <div className="detail-item">
            <label title={VMA_FIELD_HELP.wholesale_demand_level}>Wholesale Demand</label>
            <span>{str(vehicle.wholesale_demand_level)}</span>
          </div>
          <div className="detail-item">
            <label title={VMA_FIELD_HELP.wholesale_avg_days_to_sell}>WS Days to Sell</label>
            <span>{num(vehicle.wholesale_avg_days_to_sell)}</span>
          </div>
          <div className="detail-item">
            <label title={VMA_FIELD_HELP.wholesale_days_supply}>WS Days Supply</label>
            <span>{num(vehicle.wholesale_days_supply)}</span>
          </div>
          <div className="detail-item">
            <label title={VMA_FIELD_HELP.wholesale_sales_efficiency}>WS Sales Efficiency</label>
            <span>{pct(vehicle.wholesale_sales_efficiency)}</span>
          </div>
          <div className="detail-item">
            <label title={VMA_FIELD_HELP.wholesale_sales_effectiveness}>WS Sales Effectiveness</label>
            <span>{pct(vehicle.wholesale_sales_effectiveness)}</span>
          </div>
          <div className="detail-item">
            <label title={VMA_FIELD_HELP.buyer_coverage}>Buyer Coverage</label>
            <span>{num(vehicle.buyer_coverage)}</span>
          </div>
          <div className="detail-item">
            <label>Cohort</label>
            <span>
              {[vehicle.cohort_year_v4,
                vehicle.cohort_mileage_v4 ? `${vehicle.cohort_mileage_v4} mi` : null,
                vehicle.cohort_condition_v4 ? `CR ${vehicle.cohort_condition_v4}` : null]
                .filter(Boolean).join(' · ') || 'N/A'}
            </span>
          </div>
          <div className="detail-item">
            <label title={VMA_FIELD_HELP.retail_supply_level}>Retail Supply</label>
            <span>{str(vehicle.retail_supply_level)}</span>
          </div>
          <div className="detail-item">
            <label title={VMA_FIELD_HELP.retail_demand_level}>Retail Demand</label>
            <span>{str(vehicle.retail_demand_level)}</span>
          </div>
          <div className="detail-item">
            <label title={VMA_FIELD_HELP.retail_avg_days_to_sell}>Retail Days to Sell</label>
            <span>{num(vehicle.retail_avg_days_to_sell)}</span>
          </div>
          <div className="detail-item">
            <label title={VMA_FIELD_HELP.retail_days_supply}>Retail Days Supply</label>
            <span>{num(vehicle.retail_days_supply)}</span>
          </div>
          <div className="detail-item">
            <label title={VMA_FIELD_HELP.retail_rmi}>Retail RMI</label>
            <span>{money(vehicle.retail_rmi)}</span>
          </div>
          <div className="detail-item">
            <label title={VMA_FIELD_HELP.retail_spread}>Retail Spread</label>
            <span>{money(vehicle.retail_spread)}</span>
          </div>
          <div className="detail-item">
            <label title={VMA_FIELD_HELP.retail_retention}>Retail Retention</label>
            <span>{vehicle.retail_retention != null ? `${(vehicle.retail_retention * 100).toFixed(1)}%` : 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label title={VMA_FIELD_HELP.retail_avg_list_price}>Retail List (avg)</label>
            <span>{money(vehicle.retail_avg_list_price)}</span>
          </div>
          <div className="detail-item">
            <label>Retail Value</label>
            <span>{money(vehicle.retail_value)}</span>
          </div>
          <div className="detail-item">
            <label>Retail Value Range</label>
            <span>{str(vehicle.retail_value_range)}</span>
          </div>
        </div>

        {!hasSignal && (
          <div className="no-data-message">No market signal for this vehicle's year/make/model.</div>
        )}
        <p className="modal-footnote">{VMA_PANEL_NOTE}</p>
      </div>
    );
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'where-to-focus':
        return renderWhereToFocus();
      case 'vehicle-details':
        return renderVehicleDetails();
      case 'listing-details':
        return renderListingDetails();
      case 'inspection-details':
        return renderInspectionDetails();
      case 'bid-details':
        return renderBidDetails();
      case 'buyers':
        return renderBuyers();
      case 'market-insights':
        return renderMarketInsights();
      default:
        return renderVehicleDetails();
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="vehicle-insights-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="modal-title">
            <h2>Vehicle Insights</h2>
            <span className="vehicle-identifier">
              {vehicle.model_year} {vehicle.make} {vehicle.model} - {vehicle.vin}
            </span>
          </div>
          <button className="close-button" onClick={onClose}>
            <span>&times;</span>
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`tab-button ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className="tab-number">{tab.number}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="modal-content">
          {isLoading ? (
            <div className="modal-loading-container">
              <div className="modal-loading-spinner"></div>
              <p className="modal-loading-text">Loading vehicle details...</p>
            </div>
          ) : (
            renderActiveTab()
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};